import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  LayoutDashboard, Database, FileText, QrCode, CheckCircle2, ClipboardCheck, Package, FolderKanban, ClipboardList,
  BarChart3, Receipt, Users, History, Settings as SettingsIcon, Menu, Search, Bell, Sparkles, LogOut, X, Globe, FileUp, Monitor, KeyRound, CalendarDays, Factory,
} from "lucide-react";
import Logo, { LogoMark } from "./Logo";
import { ChangePasswordDialog } from "./PasswordDialogs";
import AICopilot from "./AICopilot";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { cn, fmtNum } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, id: "Dashboard", zh: "看板" },
  { to: "/master-data", icon: Database, id: "Master Data", zh: "主数据" },
  { to: "/projects", icon: FolderKanban, id: "Master Projek", zh: "项目主数据" },
  { to: "/delivery-list-proyek", icon: ClipboardList, id: "Delivery List", zh: "项目交付清单" },
  { to: "/spk", icon: FileText, id: "SPK", zh: "工作指令" },
  { to: "/delivery-list", icon: FileUp, id: "Upload SPK", zh: "派工单上传" },
  { to: "/barcode", icon: QrCode, id: "Barcode", zh: "条码" },
  { to: "/barcode-riwayat", icon: History, id: "Riwayat Cetak", zh: "打印记录", sub: true },
  { to: "/qc", icon: CheckCircle2, id: "QC", zh: "质检" },
  { to: "/stb", icon: ClipboardCheck, id: "STB", zh: "交换单" },
  { to: "/stb-riwayat", icon: CalendarDays, id: "History Progress", zh: "进度记录", sub: true },
  { to: "/material-control", icon: Package, id: "Material Control", zh: "物料控制" },
  { to: "/laporan", icon: BarChart3, id: "Laporan", zh: "报表" },
  { to: "/laporan-harian", icon: CalendarDays, id: "Laporan Harian Produksi", zh: "每日产量报表", sub: true },
  { to: "/monitoring-proyek", icon: Monitor, id: "Monitoring Proyek", zh: "项目监控" },
  { to: "/monitoring-vendor", icon: Users, id: "Monitoring Vendor", zh: "供应商总结", sub: true },
  { to: "/monitoring-area", icon: Factory, id: "Monitoring Area", zh: "区域监控", sub: true },
  { to: "/tagihan", icon: Receipt, id: "Tagihan Vendor", zh: "供应商结算" },
  { to: "/users", icon: Users, id: "User Management", zh: "用户管理" },
  { to: "/audit", icon: History, id: "Revisi / Audit", zh: "修订/审计" },
  { to: "/settings", icon: SettingsIcon, id: "Pengaturan", zh: "系统设置" },
];

const LANGS = { bi: "ID + 中文", id: "ID", zh: "中文" };

function SidebarContent({ onNavigate, onChangePassword }) {
  const { user } = useApp();
  return (
    <div className="flex h-full flex-col">
      <div className="px-4 py-5 border-b border-slate-800/70">
        <Logo compact={false} />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="sidebar-nav">
        {NAV.map((n) => (
          <NavLink
            key={n.to} to={n.to} onClick={onNavigate}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded px-3 py-2 text-[13px] transition-colors",
              n.sub && "ml-5 py-1.5 text-[12px]",
              isActive
                ? "bg-gradient-to-r from-sky-600/25 to-transparent text-sky-300 border-l-2 border-sky-400"
                : "text-slate-400 border-l-2 border-transparent hover:bg-slate-800/50 hover:text-slate-200"
            )}
          >
            {({ isActive }) => (
              <>
                <n.icon className={cn("h-4 w-4 shrink-0", isActive && "text-sky-400")} />
                <span className="truncate"><span className="i18n-id">{n.id} </span><span className="cn text-[10px] text-slate-500">{n.zh}</span></span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800/70 p-3 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 font-display text-sm font-bold text-[#0B1220]">
          {(user?.name || "W").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[13px] font-medium text-slate-200">{user?.name}</div>
          <div className="truncate font-mono text-[11px] text-slate-500">@{user?.username} · <span className="capitalize">{user?.role}</span></div>
        </div>
        <button type="button" onClick={onChangePassword} title="Ganti password" className="rounded p-1.5 text-slate-500 hover:bg-amber-500/10 hover:text-amber-300 transition-colors" data-testid="sidebar-change-password-button">
          <KeyRound className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, lang, setLang, search, setSearch } = useApp();
  const [drawer, setDrawer] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [bell, setBell] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [period, setPeriod] = useState("…");
  const loc = useLocation();

  useEffect(() => { setDrawer(false); setBell(false); window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => { api.get("/settings").then((r) => setPeriod(r.data.periodLabel)).catch(() => {}); }, []);
  const [dismissedCount, setDismissedCount] = useState(0);
  const loadAlerts = () => api.get("/alerts").then((r) => { setAlerts(r.data.alerts || []); setDismissedCount(r.data.dismissed || 0); }).catch(() => {});
  useEffect(() => {
    loadAlerts();
    const t = setInterval(loadAlerts, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loc.pathname]);
  const dismissAlert = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.post(`/alerts/${id}/dismiss`); await loadAlerts(); } catch (err) { /* noop */ }
  };
  const restoreAlerts = async () => { try { await api.post("/alerts/restore"); await loadAlerts(); } catch (err) { /* noop */ } };

  const openBell = async () => {
    if (!bell) {
      try { const { data } = await api.get("/audit?limit=6"); setNotifs(data); } catch (e) { /* noop */ }
    }
    setBell(!bell);
  };

  return (
    <div className="min-h-screen bg-[#07090E]">
      {/* desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 border-r border-slate-800/70 bg-[#090B10] lg:block" data-testid="sidebar">
        <SidebarContent onChangePassword={() => setPwdOpen(true)} />
      </aside>

      {/* mobile drawer */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setDrawer(false)} />
            <motion.aside initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed inset-y-0 left-0 z-50 w-60 border-r border-slate-800 bg-[#090B10] lg:hidden" data-testid="sidebar-drawer">
              <button onClick={() => setDrawer(false)} className="absolute right-2 top-3 rounded p-1.5 text-slate-400 hover:bg-slate-800" data-testid="sidebar-drawer-close-button"><X className="h-4 w-4" /></button>
              <SidebarContent onNavigate={() => setDrawer(false)} onChangePassword={() => { setDrawer(false); setPwdOpen(true); }} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-60">
        {/* topbar */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-slate-800 bg-[#090B10]/90 px-3 md:px-5 backdrop-blur-md">
          <button onClick={() => setDrawer(true)} className="rounded p-2 text-slate-400 hover:bg-slate-800 lg:hidden" data-testid="sidebar-open-button"><Menu className="h-5 w-5" /></button>

          <div className="relative hidden md:block w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari proyek, marking, kode…"
              className="w-full rounded bg-slate-900/80 border border-slate-800 py-1.5 pl-8 pr-3 text-[13px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500/60"
              data-testid="topbar-search-input"
            />
          </div>

          <div className="ml-auto flex items-center gap-1.5 md:gap-2.5">
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded border border-slate-800 bg-slate-900/80 px-2.5 py-1 text-[11px] text-slate-400 font-mono" data-testid="period-chip">
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />{period}
            </span>

            <button onClick={() => setLang(lang === "bi" ? "id" : lang === "id" ? "zh" : "bi")}
              className="inline-flex items-center gap-1.5 rounded border border-slate-800 bg-slate-900/80 px-2.5 py-1.5 text-[11px] font-mono text-slate-300 hover:border-sky-500/50 transition-colors" data-testid="lang-toggle-button">
              <Globe className="h-3.5 w-3.5 text-sky-400" />{LANGS[lang]}
            </button>

            <div className="relative">
              <button onClick={openBell} className="relative rounded p-2 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors" data-testid="notification-bell-button">
                <Bell className={`h-[18px] w-[18px] ${alerts.length ? "text-rose-400" : ""}`} />
                {alerts.length > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white animate-pulse" data-testid="alert-count-badge">{alerts.length}</span>}
              </button>
              <AnimatePresence>
                {bell && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    className="absolute right-0 top-11 w-80 rounded border border-slate-800 bg-[#0D1524] shadow-2xl p-2" data-testid="notification-dropdown">
                    <div className="px-2 py-1.5 text-xs font-medium text-rose-300">Peringatan Target STB / 交接目标预警</div>
                    {alerts.length === 0 && <div className="mx-2 mb-1 rounded border border-emerald-500/20 bg-emerald-500/5 px-2 py-2 text-[11px] text-emerald-300" data-testid="alerts-ok">{dismissedCount ? `${dismissedCount} peringatan ditandai dibaca — muncul lagi bila angka STB berubah` : "Semua target STB bulan ini terpenuhi ✓"}</div>}
                    {dismissedCount > 0 && <button onClick={restoreAlerts} className="mx-2 mb-1 text-[10px] text-sky-400 hover:underline" data-testid="alerts-restore-button">Tampilkan kembali yang sudah dibaca ({dismissedCount})</button>}
                    {alerts.map((a) => (
                      <Link key={a.id} to={a.link} onClick={() => setBell(false)}
                        className={`relative mb-1 block rounded border px-2 py-2 pr-7 transition-colors ${a.severity === "high" ? "border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15" : "border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10"}`}
                        data-testid={`alert-${a.type}`}>
                        <button onClick={(e) => dismissAlert(e, a.id)} title="Tandai sudah dibaca / 标记已读" className="absolute right-1.5 top-1.5 rounded p-0.5 text-slate-500 hover:bg-slate-700 hover:text-slate-200" data-testid={`alert-dismiss-${a.type}`}><X className="h-3 w-3" /></button>
                        <div className="flex items-center justify-between text-[12px] text-slate-100">
                          <span className="font-medium">{a.title}</span>
                          <span className={`font-mono text-[10px] ${a.severity === "high" ? "text-rose-300" : "text-amber-300"}`}>{a.pct}%</span>
                        </div>
                        <div className="text-[11px] text-slate-400">{a.detail} <span className="cn text-slate-500">{a.zh}</span></div>
                        <div className="mt-1 h-1 overflow-hidden rounded bg-slate-800"><div className={`h-full ${a.severity === "high" ? "bg-rose-500" : "bg-amber-400"}`} style={{ width: `${Math.min(100, a.pct)}%` }} /></div>
                      </Link>
                    ))}
                    <div className="mt-1 border-t border-slate-800 px-2 py-1.5 text-xs font-medium text-slate-400">Aktivitas Terbaru / 最新活动</div>
                    {notifs.slice(0, 6).map((n) => (
                      <div key={n.id} className="rounded px-2 py-2 hover:bg-slate-800/60">
                        <div className="text-[12px] text-slate-200"><span className="font-mono text-sky-400">{n.action}</span> · {n.name}</div>
                        <div className="text-[11px] text-slate-500">{n.detail} · {n.by}</div>
                      </div>
                    ))}
                    {notifs.length === 0 && <div className="px-2 py-4 text-center text-xs text-slate-600">Belum ada aktivitas</div>}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button onClick={() => setAiOpen(true)}
              className="inline-flex items-center gap-1.5 rounded bg-gradient-to-r from-sky-600 to-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-sky-900/40 hover:from-sky-500 hover:to-indigo-500 transition-colors" data-testid="ai-copilot-open-button">
              <Sparkles className="h-3.5 w-3.5" /><span className="hidden sm:inline">HL-Copilot</span>
            </button>

            <button onClick={() => setPwdOpen(true)} title="Ganti password"
              className="rounded p-2 text-slate-400 hover:bg-amber-500/10 hover:text-amber-300 transition-colors" data-testid="header-change-password-button">
              <KeyRound className="h-[18px] w-[18px]" />
            </button>
            <button onClick={logout} title="Keluar"
              className="rounded p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-colors" data-testid="logout-button">
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1560px] px-3 py-5 md:px-6 md:py-7">
          {children}
        </main>
      </div>

      <AICopilot open={aiOpen} setOpen={setAiOpen} />
      <ChangePasswordDialog open={pwdOpen || !!user?.mustChangePassword} force={!!user?.mustChangePassword} onClose={() => setPwdOpen(false)} />
    </div>
  );
}